<script setup lang="ts">
import { onBeforeMount, ref, watch } from "vue";
import { useRoute } from "vue-router";

import { useAccess } from "@/composables/access";
import { type MenuItem, useLayout } from "@/layout/composables/layout";
import type { Permission, Resource, ResourceActions } from "@/services/PermissionsService";

const route = useRoute();

const { layoutConfig, layoutState, setActiveMenuItem } = useLayout();

const {
  item,
  index = 0,
  root = true,
  parentItemKey = null,
} = defineProps<{
  item: MenuItem;
  index?: number;
  root?: boolean;
  parentItemKey?: string | null;
}>();

const isActiveMenu = ref(false);
const itemKey = ref<string | null>(null);

onBeforeMount(() => {
  itemKey.value = parentItemKey ? `${parentItemKey}-${index}` : String(index);

  const activeItem = layoutState.activeMenuItem;

  isActiveMenu.value = activeItem === itemKey.value || activeItem ? activeItem.startsWith(`${itemKey.value}-`) : false;
  handleRouteChange(route.path);
});

watch(
  () => layoutState.activeMenuItem,
  (newVal) => {
    isActiveMenu.value = newVal === itemKey.value || (newVal?.startsWith(`${itemKey.value}-`) ?? false);
  },
);

watch(
  () => layoutConfig.menuMode,
  () => {
    isActiveMenu.value = false;
  },
);

watch(
  () => layoutState.overlaySubmenuActive,
  (newValue) => {
    if (!newValue) {
      isActiveMenu.value = false;
    }
  },
);
watch(
  () => route.path,
  (newPath) => {
    if (item.to && item.to === newPath) {
      setActiveMenuItem(itemKey);
    }
  },
);
watch(() => route.path, handleRouteChange);

function handleRouteChange(newPath: string): void {
  if (item.to && item.to === newPath) {
    setActiveMenuItem(itemKey);
  }
}

function itemClick(item: MenuItem): void {
  if (item.items) {
    setActiveMenuItem(isActiveMenu.value ? parentItemKey : itemKey);
  } else {
    setActiveMenuItem(itemKey);
  }
}

function checkActiveRoute(item: MenuItem): boolean {
  return route.path === item.to;
}

function hasPermission(permission: Permission): boolean {
  const [resource, action] = permission.split(":", 2) as [Resource, ResourceActions<Resource>];
  const access = useAccess(resource);
  return access.permits(action);
}

function visibleItems(items: MenuItem[]): MenuItem[] {
  return items.filter((item) => {
    if (item.items && visibleItems(item.items).length == 0) {
      return false;
    }
    if (!item.permission) {
      return true;
    }
    if (Array.isArray(item.permission)) {
      return item.permission.some(hasPermission);
    }
    return hasPermission(item.permission);
  });
}
</script>

<template>
  <li
    :class="{
      'layout-root-menuitem': root,
      'active-menuitem': isActiveMenu,
    }"
  >
    <!-- section header -->
    <div v-if="root && visibleItems(item.items as MenuItem[]).length > 0" class="layout-menuitem-root-text">
      <span>{{ item.label }}</span> <i class="layout-menuitem-root-icon" />
    </div>

    <!-- submenu -->
    <a v-if="(!item.to || item.items) && visibleItems(item.items as MenuItem[]).length > 0" @click="itemClick(item)">
      <i v-if="item.icon" :class="item.icon" class="layout-menuitem-icon" />
      <span class="layout-menuitem-text">{{ item.label }}</span>
      <i v-if="item.items" class="pi pi-fw pi-angle-down layout-submenu-toggler" />
    </a>

    <!-- link -->
    <router-link
      v-if="item.to && !item.items"
      :class="{ 'active-route': checkActiveRoute(item) }"
      :to="item.to"
      @click="itemClick(item)"
    >
      <i v-if="item.icon" :class="item.icon" class="layout-menuitem-icon" />
      <span class="layout-menuitem-text">{{ item.label }}</span>
    </router-link>

    <!-- subitems -->
    <ul v-if="item.items" :class="{ 'layout-root-submenulist': root }">
      <AppMenuItem
        v-for="(child, i) in visibleItems(item.items)"
        :key="child.label"
        :index="i"
        :item="child"
        :parent-item-key="itemKey"
        :root="false"
      />
    </ul>
  </li>
</template>

<style lang="scss" scoped>
.faded .layout-menuitem-text {
  color: var(--p-faded-color);
}
svg {
  &.layout-menuitem-icon {
    fill: currentColor;
    width: 1.28571429em;
    text-align: center;
  }
}
</style>
